import axios, { AxiosRequestConfig } from 'axios';
import {
  QueryClient,
  QueryFunction,
  QueryFunctionContext,
  MutationFunction,
  MutationOptions,
} from 'react-query';
import { TcsHeaders } from '@tcs/common/http/tcs-headers';
import { OktaAuth } from '@okta/okta-auth-js';
import formatUrl from '@tcs/common/utils/formatUrl';
export { useQuery, useQueryClient } from 'react-query';
export type { MutationKey } from 'react-query/types/core';

enum STATUS_CODES {
  UNAUTHORIZED = 401,
}

const maxRetryCount = 2;

const createRequestConfig = async (
  oktaAuth: OktaAuth
): Promise<AxiosRequestConfig> => {
  const config: AxiosRequestConfig = { headers: {} };
  const tokens = await oktaAuth.tokenManager.getTokens();

  if (tokens?.accessToken?.accessToken) {
    config.headers[TcsHeaders.Authorization] = tokens.accessToken.accessToken;
  }

  return config;
};

const createQueryFn =
  (oktaAuth: OktaAuth, apiBaseUrl: string) =>
  async <TQueryResponse>({
    queryKey,
  }: QueryFunctionContext): Promise<QueryFunction<TQueryResponse>> => {
    try {
      const config = await createRequestConfig(oktaAuth);

      const { data } = await axios.get(
        `${formatUrl(apiBaseUrl)}${queryKey}`,
        config
      );

      return data;
    } catch (error) {
      if (error.response.status === STATUS_CODES.UNAUTHORIZED) {
        await oktaAuth.signOut();
      }
      throw error;
    }
  };

const createMutationFn =
  (oktaAuth: OktaAuth, apiBaseUrl: string) =>
  async ({
    mutationKey,
    ...rest
  }: MutationOptions): Promise<MutationFunction<any, any>> => {
    try {
      const config = await createRequestConfig(oktaAuth);
      const { data } = await axios.put(
        `${formatUrl(apiBaseUrl)}${mutationKey}`,
        { ...rest },
        config
      );

      return data;
    } catch (error) {
      if (error.response.status === STATUS_CODES.UNAUTHORIZED) {
        await oktaAuth.signOut();
      }
      throw error;
    }
  };

export const queryClient = (oktaAuth: OktaAuth, apiBaseUrl: string) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: createQueryFn(oktaAuth, apiBaseUrl),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: maxRetryCount,
      },
      mutations: {
        mutationFn: createMutationFn(oktaAuth, apiBaseUrl),
        retry: false,
      },
    },
  });
